import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Card, Button, Input, Select, Label } from "../components/ui-kit";
import { FileDown, FileSpreadsheet, FileArchive, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { thisMonthIST, todayIST } from "../lib/datefmt";

export default function ReportsPage() {
  const { user } = useAuth();
  const canAdminReports = ["admin", "manager", "director", "rm_and_above", "sales_manager"].includes(user?.role);
  const canDownloadAll = ["admin", "manager", "director"].includes(user?.role);
  const [users, setUsers] = useState([]);
  const [downloadAllRange, setDownloadAllRange] = useState({ date_from: "", date_to: "" });
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [orderForm, setOrderForm] = useState({ date_from: "", date_to: "", user_id: "", status: "" });
  const [period, setPeriod] = useState(thisMonthIST());
  const [fmForm, setFmForm] = useState({ date_from: "", date_to: "" });
  const [visitForm, setVisitForm] = useState({ date_from: "", date_to: "", user_id: "", kind: "" });
  const [dailyForm, setDailyForm] = useState({ date_from: "", date_to: "", user_id: "", mode: "" });
  const [expenseForm, setExpenseForm] = useState({ date_from: "", date_to: "", user_id: "", status: "" });
  const [collForm, setCollForm] = useState({ date_from: "", date_to: "", user_id: "", status: "" });
  const [zipDaily, setZipDaily] = useState({ date_from: "", date_to: "", user_id: "" });
  const [zipExpense, setZipExpense] = useState({ date_from: "", date_to: "", user_id: "" });
  const [leadForm, setLeadForm] = useState({ date_from: "", date_to: "", user_id: "", status: "" });

  useEffect(() => {
    (async () => {
      try {
        setUsers((await api.get("/users")).data);
      } catch {
        setUsers([]);
      }
    })();
    // One-time load; users list rarely changes during a session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async (path, params, filename) => {
    try {
      const res = await api.get(path, { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div>
      <PageHeader title="Reports" subtitle="Download Excel reports for orders, targets, farmer meetings and field visits" />

      {canDownloadAll && (
        <Card className="mb-6 border-2 border-[#1E3F20]/20 bg-gradient-to-br from-[#1E3F20]/5 to-white" data-testid="download-all-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#1E3F20] text-white grid place-items-center">
              <Sparkles className="w-5 h-5" strokeWidth={2.25} />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-xl font-bold">Download Everything</h3>
              <p className="text-sm text-[#5C6B5E] mt-1">
                Single ZIP with all masters (users, customers, products, farmers, price lists), all
                transactions (orders, collections, leads, demos, daily reports, expenses, leaves) plus
                every attached photo. Optional date filter applies to transactions & photos.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <Label>From (optional)</Label>
              <Input
                type="date"
                value={downloadAllRange.date_from}
                onChange={(e) => setDownloadAllRange((v) => ({ ...v, date_from: e.target.value }))}
                data-testid="download-all-from"
              />
            </div>
            <div>
              <Label>To (optional)</Label>
              <Input
                type="date"
                value={downloadAllRange.date_to}
                onChange={(e) => setDownloadAllRange((v) => ({ ...v, date_to: e.target.value }))}
                data-testid="download-all-to"
              />
            </div>
            <Button
              disabled={downloadingAll}
              data-testid="download-all-btn"
              onClick={async () => {
                setDownloadingAll(true);
                try {
                  await download(
                    "/reports/download-all.zip",
                    {
                      ...(downloadAllRange.date_from && { date_from: downloadAllRange.date_from }),
                      ...(downloadAllRange.date_to && { date_to: downloadAllRange.date_to }),
                    },
                    `taraigold_full_export_${todayIST()}.zip`,
                  );
                  toast.success("Export downloaded");
                } finally {
                  setDownloadingAll(false);
                }
              }}
            >
              <FileArchive className="w-4 h-4" />
              {downloadingAll ? "Preparing…" : "Download all data"}
            </Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {canAdminReports && (
        <>
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <FileSpreadsheet className="w-5 h-5 text-[#1E3F20]" />
            <h3 className="font-heading text-xl font-bold">Orders</h3>
          </div>
          <div className="space-y-3">
            <div>
              <Label>From</Label>
              <Input type="date" value={orderForm.date_from} onChange={(e) => setOrderForm({ ...orderForm, date_from: e.target.value })} />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={orderForm.date_to} onChange={(e) => setOrderForm({ ...orderForm, date_to: e.target.value })} />
            </div>
            <div>
              <Label>Sales person</Label>
              <Select value={orderForm.user_id} onChange={(e) => setOrderForm({ ...orderForm, user_id: e.target.value })}>
                <option value="">All</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={orderForm.status} onChange={(e) => setOrderForm({ ...orderForm, status: e.target.value })}>
                <option value="">Any</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="dispatched">Dispatched</option>
              </Select>
            </div>
            <Button className="w-full" onClick={() => download("/reports/orders.xlsx", orderForm, "orders_report.xlsx")} data-testid="dl-orders">
              <FileDown className="w-4 h-4" /> Download Excel
            </Button>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-4">
            <FileSpreadsheet className="w-5 h-5 text-[#1E3F20]" />
            <h3 className="font-heading text-xl font-bold">Targets</h3>
          </div>
          <div className="space-y-3">
            <div>
              <Label>Period</Label>
              <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
            </div>
            <p className="text-sm text-[#5C6B5E]">Two sheets: Order targets vs achievement, Collection targets vs payments.</p>
            <Button className="w-full" onClick={() => download("/reports/targets.xlsx", { period }, "targets_report.xlsx")} data-testid="dl-targets">
              <FileDown className="w-4 h-4" /> Download Excel
            </Button>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-4">
            <FileSpreadsheet className="w-5 h-5 text-[#1E3F20]" />
            <h3 className="font-heading text-xl font-bold">Farmer meetings</h3>
          </div>
          <div className="space-y-3">
            <div>
              <Label>From</Label>
              <Input type="date" value={fmForm.date_from} onChange={(e) => setFmForm({ ...fmForm, date_from: e.target.value })} />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={fmForm.date_to} onChange={(e) => setFmForm({ ...fmForm, date_to: e.target.value })} />
            </div>
            <Button className="w-full" onClick={() => download("/reports/farmer-meetings.xlsx", fmForm, "farmer_meetings_report.xlsx")} data-testid="dl-fm">
              <FileDown className="w-4 h-4" /> Download Excel
            </Button>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-4">
            <FileSpreadsheet className="w-5 h-5 text-[#1E3F20]" />
            <h3 className="font-heading text-xl font-bold">Field Visits</h3>
          </div>
          <div className="space-y-3">
            <div>
              <Label>From</Label>
              <Input type="date" value={visitForm.date_from} onChange={(e) => setVisitForm({ ...visitForm, date_from: e.target.value })} data-testid="visit-date-from" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={visitForm.date_to} onChange={(e) => setVisitForm({ ...visitForm, date_to: e.target.value })} data-testid="visit-date-to" />
            </div>
            <div>
              <Label>Sales person</Label>
              <Select value={visitForm.user_id} onChange={(e) => setVisitForm({ ...visitForm, user_id: e.target.value })} data-testid="visit-user">
                <option value="">All</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Visit type</Label>
              <Select value={visitForm.kind} onChange={(e) => setVisitForm({ ...visitForm, kind: e.target.value })} data-testid="visit-kind">
                <option value="">All</option>
                <option value="dealer">Dealer</option>
                <option value="farmer">Farmer</option>
                <option value="new_lead">New lead</option>
                <option value="new_farmer">New farmer</option>
              </Select>
            </div>
            <Button className="w-full" onClick={() => download("/reports/visits.xlsx", visitForm, "visits_report.xlsx")} data-testid="dl-visits">
              <FileDown className="w-4 h-4" /> Download Excel
            </Button>
          </div>
        </Card>
        </>
        )}

        <Card>
          <div className="flex items-center gap-2 mb-4">
            <FileSpreadsheet className="w-5 h-5 text-[#1E3F20]" />
            <h3 className="font-heading text-xl font-bold">Daily Report</h3>
          </div>
          <div className="space-y-3">
            <div>
              <Label>From</Label>
              <Input type="date" value={dailyForm.date_from} onChange={(e) => setDailyForm({ ...dailyForm, date_from: e.target.value })} data-testid="daily-date-from" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={dailyForm.date_to} onChange={(e) => setDailyForm({ ...dailyForm, date_to: e.target.value })} data-testid="daily-date-to" />
            </div>
            <div>
              <Label>Sales person</Label>
              <Select value={dailyForm.user_id} onChange={(e) => setDailyForm({ ...dailyForm, user_id: e.target.value })} data-testid="daily-user">
                <option value="">All</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Mode</Label>
              <Select value={dailyForm.mode} onChange={(e) => setDailyForm({ ...dailyForm, mode: e.target.value })} data-testid="daily-mode">
                <option value="">All</option>
                <option value="field_day">Field day</option>
                <option value="meeting">Meeting</option>
                <option value="work_from_home">Work from home</option>
              </Select>
            </div>
            <Button className="w-full" onClick={() => download("/reports/daily-report.xlsx", dailyForm, "daily_report.xlsx")} data-testid="dl-daily">
              <FileDown className="w-4 h-4" /> Download Excel
            </Button>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-4">
            <FileSpreadsheet className="w-5 h-5 text-[#1E3F20]" />
            <h3 className="font-heading text-xl font-bold">Expenses</h3>
          </div>
          <div className="space-y-3">
            <div>
              <Label>From</Label>
              <Input type="date" value={expenseForm.date_from} onChange={(e) => setExpenseForm({ ...expenseForm, date_from: e.target.value })} data-testid="expense-date-from" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={expenseForm.date_to} onChange={(e) => setExpenseForm({ ...expenseForm, date_to: e.target.value })} data-testid="expense-date-to" />
            </div>
            <div>
              <Label>Sales person</Label>
              <Select value={expenseForm.user_id} onChange={(e) => setExpenseForm({ ...expenseForm, user_id: e.target.value })} data-testid="expense-user">
                <option value="">All</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={expenseForm.status} onChange={(e) => setExpenseForm({ ...expenseForm, status: e.target.value })} data-testid="expense-status">
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </Select>
            </div>
            <Button className="w-full" onClick={() => download("/reports/expenses.xlsx", expenseForm, "expenses_report.xlsx")} data-testid="dl-expenses">
              <FileDown className="w-4 h-4" /> Download Excel
            </Button>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-4">
            <FileSpreadsheet className="w-5 h-5 text-[#1E3F20]" />
            <h3 className="font-heading text-xl font-bold">Collections</h3>
          </div>
          <div className="space-y-3">
            <div>
              <Label>From</Label>
              <Input type="date" value={collForm.date_from} onChange={(e) => setCollForm({ ...collForm, date_from: e.target.value })} data-testid="coll-date-from" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={collForm.date_to} onChange={(e) => setCollForm({ ...collForm, date_to: e.target.value })} data-testid="coll-date-to" />
            </div>
            <div>
              <Label>Sales person</Label>
              <Select value={collForm.user_id} onChange={(e) => setCollForm({ ...collForm, user_id: e.target.value })} data-testid="coll-user">
                <option value="">All</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={collForm.status} onChange={(e) => setCollForm({ ...collForm, status: e.target.value })} data-testid="coll-status">
                <option value="">All</option>
                <option value="received">Received</option>
                <option value="pending">Pending</option>
                <option value="not_received">Not received</option>
                <option value="declined">Declined</option>
              </Select>
            </div>
            <Button className="w-full" onClick={() => download("/reports/collections.xlsx", collForm, "collections_report.xlsx")} data-testid="dl-collections">
              <FileDown className="w-4 h-4" /> Download Excel
            </Button>
            <Button variant="outline" className="w-full" onClick={() => download("/reports/collection-photos.zip", collForm, "collection_photos.zip")} data-testid="dl-collection-photos">
              <FileDown className="w-4 h-4" /> Download Receipt Photos (ZIP)
            </Button>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-4">
            <FileSpreadsheet className="w-5 h-5 text-[#1E3F20]" />
            <h3 className="font-heading text-xl font-bold">Daily Report Photos</h3>
          </div>
          <div className="space-y-3">
            <div>
              <Label>From</Label>
              <Input type="date" value={zipDaily.date_from} onChange={(e) => setZipDaily({ ...zipDaily, date_from: e.target.value })} data-testid="dr-zip-date-from" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={zipDaily.date_to} onChange={(e) => setZipDaily({ ...zipDaily, date_to: e.target.value })} data-testid="dr-zip-date-to" />
            </div>
            <div>
              <Label>Sales person</Label>
              <Select value={zipDaily.user_id} onChange={(e) => setZipDaily({ ...zipDaily, user_id: e.target.value })} data-testid="dr-zip-user">
                <option value="">All</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </Select>
            </div>
            <Button className="w-full" onClick={() => download("/reports/daily-report-photos.zip", zipDaily, "daily_report_photos.zip")} data-testid="dl-dr-photos">
              <FileDown className="w-4 h-4" /> Download Start/End & Visit Photos (ZIP)
            </Button>
            <div className="text-[11px] text-[#5C6B5E]">Includes start KM, end KM, and dealer/farmer/lead visit photos. Files renamed to IST timestamp.</div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-4">
            <FileSpreadsheet className="w-5 h-5 text-[#1E3F20]" />
            <h3 className="font-heading text-xl font-bold">Expense Photos</h3>
          </div>
          <div className="space-y-3">
            <div>
              <Label>From</Label>
              <Input type="date" value={zipExpense.date_from} onChange={(e) => setZipExpense({ ...zipExpense, date_from: e.target.value })} data-testid="exp-zip-date-from" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={zipExpense.date_to} onChange={(e) => setZipExpense({ ...zipExpense, date_to: e.target.value })} data-testid="exp-zip-date-to" />
            </div>
            <div>
              <Label>Sales person</Label>
              <Select value={zipExpense.user_id} onChange={(e) => setZipExpense({ ...zipExpense, user_id: e.target.value })} data-testid="exp-zip-user">
                <option value="">All</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </Select>
            </div>
            <Button className="w-full" onClick={() => download("/reports/expense-photos.zip", zipExpense, "expense_photos.zip")} data-testid="dl-expense-photos">
              <FileDown className="w-4 h-4" /> Download Invoice Photos (ZIP)
            </Button>
            <div className="text-[11px] text-[#5C6B5E]">Night-stay invoices from approved / pending claims. Files renamed to IST timestamp.</div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-4">
            <FileSpreadsheet className="w-5 h-5 text-[#1E3F20]" />
            <h3 className="font-heading text-xl font-bold">Leads</h3>
          </div>
          <div className="space-y-3">
            <div>
              <Label>From</Label>
              <Input type="date" value={leadForm.date_from} onChange={(e) => setLeadForm({ ...leadForm, date_from: e.target.value })} data-testid="lead-date-from" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={leadForm.date_to} onChange={(e) => setLeadForm({ ...leadForm, date_to: e.target.value })} data-testid="lead-date-to" />
            </div>
            <div>
              <Label>Sales person</Label>
              <Select value={leadForm.user_id} onChange={(e) => setLeadForm({ ...leadForm, user_id: e.target.value })} data-testid="lead-user">
                <option value="">All (my hierarchy)</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={leadForm.status} onChange={(e) => setLeadForm({ ...leadForm, status: e.target.value })} data-testid="lead-status">
                <option value="">All</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="qualified">Qualified</option>
                <option value="won">Won</option>
                <option value="lost">Lost</option>
              </Select>
            </div>
            <Button className="w-full" onClick={() => download("/reports/leads.xlsx", leadForm, "leads_report.xlsx")} data-testid="dl-leads">
              <FileDown className="w-4 h-4" /> Download Excel
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
